/**
 * AdvertiserInvoicesPage — self-serve billing-document browser for a given
 * advertiser. Read-only report: enter advertiser id + optional year, fetch
 * invoices, show footer totals. Numbers-only table, one click to resolve the
 * PDF URL for an issued/paid invoice.
 *
 * Brand invariants (W9-29):
 *   - GREEN only (green-600 / green-700 / green-50 / green-100).
 *   - White / zinc surface. Inter font. Rounded 6-8px. Plain Tailwind.
 *   - No emerald, sky, blue, orange, purple, dark mode, or emojis.
 */
import { useMemo, useState, type FormEvent } from "react";
import { useAdvertiserInvoicesList, useInvoicePdf } from "./hooks";
import type { AdvertiserInvoiceRow, FooterTotals, InvoiceStatus } from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const TH =
	"px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-zinc-500";
const TD = "px-3 py-2 text-sm text-zinc-900";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const LABEL = "text-xs font-medium uppercase tracking-wider text-zinc-600";
const BTN_PRIMARY =
	"inline-flex items-center justify-center gap-2 rounded-[6px] bg-green-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-600/40 disabled:cursor-not-allowed disabled:opacity-50";
const BTN_LINK =
	"inline-flex items-center gap-1 rounded-[6px] border border-green-600 px-3 py-1 text-xs font-medium text-green-700 transition hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-50";

function formatMoney(cents: number, currency: string): string {
	const amount = cents / 100;
	if (!currency) return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	try {
		return new Intl.NumberFormat(undefined, {
			style: "currency",
			currency,
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(amount);
	} catch {
		return `${amount.toFixed(2)} ${currency}`;
	}
}

function formatPeriod(startSec: number, endSec: number): string {
	const fmt = (s: number) => {
		const d = new Date(s * 1000);
		return d.toISOString().slice(0, 10);
	};
	return `${fmt(startSec)} to ${fmt(endSec)}`;
}

const STATUS_CLASSES: Record<InvoiceStatus, string> = {
	paid: "border-green-600 bg-green-50 text-green-700",
	issued: "border-green-600 bg-white text-green-700",
	draft: "border-zinc-300 bg-zinc-50 text-zinc-600",
};

function StatusPill({ status }: { status: InvoiceStatus }) {
	return (
		<span
			className={`inline-flex items-center rounded-[6px] border px-2 py-0.5 text-xs font-medium ${STATUS_CLASSES[status]}`}
			data-testid={`advertiser-invoices-status-${status}`}
		>
			{status}
		</span>
	);
}

function DocumentIcon() {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 24 24"
			width="14"
			height="14"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
			<polyline points="14 2 14 8 20 8" />
		</svg>
	);
}

function SearchIcon() {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 24 24"
			width="14"
			height="14"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<circle cx="11" cy="11" r="8" />
			<path d="m21 21-4.3-4.3" />
		</svg>
	);
}

function TotalsFooter({ totals }: { totals: FooterTotals }) {
	const currency = totals.currency || "USD";
	return (
		<dl
			className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4"
			data-testid="advertiser-invoices-totals"
		>
			<div>
				<dt className={LABEL}>Count</dt>
				<dd className="mt-1 font-medium tabular-nums text-zinc-900">{totals.count}</dd>
			</div>
			<div>
				<dt className={LABEL}>Subtotal</dt>
				<dd className="mt-1 font-medium tabular-nums text-zinc-900">
					{formatMoney(totals.subtotal_cents, currency)}
				</dd>
			</div>
			<div>
				<dt className={LABEL}>Tax</dt>
				<dd className="mt-1 font-medium tabular-nums text-zinc-900">
					{formatMoney(totals.tax_cents, currency)}
				</dd>
			</div>
			<div>
				<dt className={LABEL}>Total</dt>
				<dd className="mt-1 font-medium tabular-nums text-green-700">
					{formatMoney(totals.total_cents, currency)}
				</dd>
			</div>
		</dl>
	);
}

function InvoiceRow({ row }: { row: AdvertiserInvoiceRow }) {
	const pdf = useInvoicePdf();
	const canPdf = row.status !== "draft" && row.pdf_url !== "";

	async function onPdf() {
		try {
			const r = await pdf.mutateAsync(row.id);
			if (typeof window !== "undefined" && r.url) {
				window.open(r.url, "_blank", "noopener,noreferrer");
			}
		} catch {
			// pdf.error is surfaced inline below
		}
	}

	return (
		<tr data-testid={`advertiser-invoices-row-${row.id}`}>
			<td className={TD}>
				<span className="font-mono text-xs text-zinc-700">{row.id}</span>
			</td>
			<td className={`${TD} tabular-nums`}>{formatPeriod(row.period_start, row.period_end)}</td>
			<td className={`${TD} text-right tabular-nums`}>
				{formatMoney(row.subtotal_cents, row.currency)}
			</td>
			<td className={`${TD} text-right tabular-nums`}>
				{formatMoney(row.tax_cents, row.currency)}
			</td>
			<td className={`${TD} text-right font-medium tabular-nums text-zinc-900`}>
				{formatMoney(row.total_cents, row.currency)}
			</td>
			<td className={TD}>
				<StatusPill status={row.status} />
			</td>
			<td className={`${TD} text-right`}>
				<button
					type="button"
					className={BTN_LINK}
					disabled={!canPdf || pdf.isPending}
					onClick={onPdf}
					data-testid={`advertiser-invoices-pdf-${row.id}`}
					aria-label={`Open PDF for invoice ${row.id}`}
				>
					<DocumentIcon />
					{pdf.isPending ? "Opening" : "PDF"}
				</button>
			</td>
		</tr>
	);
}

export function AdvertiserInvoicesPage() {
	const [advertiserIdInput, setAdvertiserIdInput] = useState("");
	const [yearInput, setYearInput] = useState("");
	const [query, setQuery] = useState<{ advertiserId: string; year?: number }>({
		advertiserId: "",
	});

	const listQuery = useAdvertiserInvoicesList(
		{ advertiserId: query.advertiserId, year: query.year },
		{ enabled: query.advertiserId.length > 0 },
	);

	const invoices = listQuery.data?.invoices ?? [];
	const totals = listQuery.data?.totals;

	const submitted = query.advertiserId.length > 0;

	const parsedYear = useMemo(() => {
		const trimmed = yearInput.trim();
		if (trimmed === "") return undefined;
		const n = Number(trimmed);
		if (!Number.isInteger(n)) return undefined;
		if (n < 1970 || n > 9999) return undefined;
		return n;
	}, [yearInput]);

	function onSubmit(e: FormEvent<HTMLFormElement>) {
		e.preventDefault();
		const id = advertiserIdInput.trim();
		if (id.length === 0) return;
		setQuery({ advertiserId: id, year: parsedYear });
	}

	return (
		<main
			data-testid="advertiser-invoices-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 font-[Inter,system-ui,sans-serif]"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-green-700">
					Billing
				</p>
				<h1 className="text-3xl font-semibold text-green-700">Advertiser invoices</h1>
				<p className="text-sm text-zinc-600">
					Self-serve access to monthly and quarterly invoices. Enter an
					advertiser id to list all issued and paid invoices.
				</p>
			</header>

			<section className={CARD} aria-label="Advertiser invoice filter">
				<form
					onSubmit={onSubmit}
					className="flex flex-col gap-4 sm:flex-row sm:items-end"
					data-testid="advertiser-invoices-filter-form"
				>
					<div className="flex flex-1 flex-col gap-1">
						<label htmlFor="advertiser-invoices-advertiser-id" className={LABEL}>
							Advertiser id
						</label>
						<input
							id="advertiser-invoices-advertiser-id"
							name="advertiser_id"
							className={INPUT}
							placeholder="adv_abc123"
							value={advertiserIdInput}
							onChange={(e) => setAdvertiserIdInput(e.target.value)}
							data-testid="advertiser-invoices-advertiser-id"
							autoComplete="off"
						/>
					</div>
					<div className="flex w-full flex-col gap-1 sm:w-40">
						<label htmlFor="advertiser-invoices-year" className={LABEL}>
							Year (optional)
						</label>
						<input
							id="advertiser-invoices-year"
							name="year"
							inputMode="numeric"
							pattern="[0-9]*"
							className={INPUT}
							placeholder="2026"
							value={yearInput}
							onChange={(e) => setYearInput(e.target.value)}
							data-testid="advertiser-invoices-year"
							autoComplete="off"
						/>
					</div>
					<button
						type="submit"
						className={BTN_PRIMARY}
						disabled={advertiserIdInput.trim().length === 0}
						data-testid="advertiser-invoices-submit"
					>
						<SearchIcon />
						Load invoices
					</button>
				</form>
			</section>

			<section className={CARD} aria-label="Advertiser invoices list">
				{!submitted ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="advertiser-invoices-prompt"
					>
						Enter an advertiser id above and load invoices to see results.
					</p>
				) : listQuery.isLoading ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="advertiser-invoices-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p
						className="text-sm text-red-600"
						role="alert"
						data-testid="advertiser-invoices-error"
					>
						{listQuery.error.message}
					</p>
				) : invoices.length === 0 ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="advertiser-invoices-empty"
					>
						No invoices on record for this advertiser
						{query.year ? ` in ${query.year}` : ""}.
					</p>
				) : (
					<div className="flex flex-col gap-6">
						<div className="overflow-x-auto">
							<table
								className="w-full border-collapse"
								data-testid="advertiser-invoices-table"
							>
								<thead className="bg-zinc-50">
									<tr>
										<th className={TH} scope="col">
											Invoice
										</th>
										<th className={TH} scope="col">
											Period
										</th>
										<th className={`${TH} text-right`} scope="col">
											Subtotal
										</th>
										<th className={`${TH} text-right`} scope="col">
											Tax
										</th>
										<th className={`${TH} text-right`} scope="col">
											Total
										</th>
										<th className={TH} scope="col">
											Status
										</th>
										<th className={`${TH} text-right`} scope="col">
											PDF
										</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-zinc-200">
									{invoices.map((row) => (
										<InvoiceRow key={row.id} row={row} />
									))}
								</tbody>
							</table>
						</div>
						{totals ? <TotalsFooter totals={totals} /> : null}
					</div>
				)}
			</section>
		</main>
	);
}

export default AdvertiserInvoicesPage;
